/**
 * Consolidated Performance Monitoring Service
 * 
 * Combines all performance monitoring capabilities into a single service:
 * - API fetch performance with baselines
 * - React Query cache operations  
 * - Component render times with anomaly detection
 * - Web Vitals (LCP, FID, CLS)
 * - Error tracking with AI suggestions
 * - Network connectivity monitoring
 * - Memory usage tracking
 * - User interaction monitoring
 * - Long task detection
 * - Performance baselines and deviation tracking
 * 
 * All logging is guarded by NODE_ENV and controlled via localStorage.
 * Production builds have ZERO performance monitoring overhead (tree-shaken).
 * 
 * DEBUG_TEMP: Modified to allow production debugging via isDebugAllowed flag
 */

import type { QueryClient } from '@tanstack/react-query';
// DEBUG_TEMP: Import debug config for production debugging
// REVERT: Remove this import when done debugging
import { isDebugAllowed } from '@/config/debug-temp';

// DEBUG_TEMP: Helper to check if performance monitoring should be allowed
// REVERT: Remove this function and restore original (process.env.NODE_ENV !== 'development') checks
function shouldAllowMonitoring(): boolean {
  return isDebugAllowed();
}

// ============================================================================
// Type Definitions
// ============================================================================

interface PerformanceBaseline {
  operationType: string;
  averageDuration: number;
  minDuration: number;
  maxDuration: number;
  count: number;
  standardDeviation: number;
}

interface PerformanceLog {
  id: string;
  timestamp: number;
  type: 'fetch' | 'query' | 'render' | 'db' | 'cache' | 'navigation' | 'interaction' | 'vitals' | 'memory' | 'error' | 'websocket' | 'network' | 'bundle' | 'longtask';
  operation: string;
  duration: number;
  metadata?: Record<string, any>;
  status: 'success' | 'error' | 'pending' | 'warning';
  url?: string;
  method?: string;
  statusCode?: number;
  cacheHit?: boolean;
  errorMessage?: string;
  lcp?: number;
  fid?: number;
  cls?: number;
  usedJSHeapSize?: number;
  totalJSHeapSize?: number;
  jsHeapSizeLimit?: number;
  targetElement?: string;
  interactionType?: string;
  routeFrom?: string;
  routeTo?: string;
  responseSize?: number;
  requestSize?: number;
  connectionType?: string;
  effectiveBandwidth?: number;
  wsState?: string;
  wsReconnectAttempts?: number;
  componentPhase?: 'mount' | 'update' | 'unmount' | 'render' | 'batch-render';
  propsChanged?: string[];
  renderMetadata?: Record<string, any>;
  pageVisible?: boolean;
  windowFocused?: boolean;
  errorStack?: string;
  correlationId?: string;
  aiAnnotation?: {
    performanceRating: 'excellent' | 'good' | 'acceptable' | 'slow' | 'critical';
    deviationFromBaseline?: number;
    isAnomaly?: boolean;
    suggestion?: string;
  };
}

// ============================================================================
// Main Performance Monitor Class
// ============================================================================

class PerformanceMonitor {
  private logs: PerformanceLog[] = [];
  private enabled: boolean = false;
  private maxLogs: number = 10000;
  private startTimes: Map<string, number> = new Map();
  private baselines: Map<string, PerformanceBaseline> = new Map();
  private correlationId?: string;

  // Monitoring states
  private webVitalsObserver: PerformanceObserver | null = null;
  private longTaskObserver: PerformanceObserver | null = null;
  private memoryIntervalId: NodeJS.Timeout | null = null;
  private isMonitoring = false;
  private lcpValue = 0;
  private clsValue = 0;
  private fidValue = 0;
  private lastMemorySnapshot: any = null;
  private recentActions: Array<{ action: string; timestamp: number }> = [];
  private maxRecentActions = 20;
  private clickStartTimes = new Map<Element, number>();
  private connectionInfo: any = null;

  // Query interceptor state
  private queryInterceptorInstalled = false;
  private originalFetch: typeof fetch;
  private queryStartTimes = new Map<string, number>();

  constructor() {
    // Always bind fetch to window context to prevent "Illegal invocation" error
    this.originalFetch = window.fetch.bind(window);
    
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return;
    
    this.enabled = localStorage.getItem('performanceLoggingEnabled') === 'true';
    this.loadBaselines();
  }

  // ============================================================================
  // Core Enable/Disable Controls
  // ============================================================================

  setEnabled(enabled: boolean): void {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return;
    
    this.enabled = enabled;
    localStorage.setItem('performanceLoggingEnabled', enabled ? 'true' : 'false');
    
    if (enabled) {
      this.startAllMonitoring();
    } else {
      this.stopAllMonitoring();
      this.clear();
    }
  }

  isEnabled(): boolean {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return false;
    return this.enabled;
  }

  // ============================================================================
  // Monitoring Lifecycle
  // ============================================================================

  startAllMonitoring(): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled || this.isMonitoring) return;
    
    this.isMonitoring = true;
    
    // Install interceptors
    this.installFetchInterceptor();
    
    // Start all monitors
    this.startWebVitalsMonitoring();
    this.startMemoryMonitoring();
    this.startInteractionMonitoring();
    this.startErrorMonitoring();
    this.startNetworkMonitoring();
    this.startLongTaskMonitoring();
    
    console.log('[Performance Monitor] All monitoring started');
  }

  stopAllMonitoring(): void {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return;
    
    this.isMonitoring = false;
    
    // Uninstall interceptors
    this.uninstallFetchInterceptor();
    
    // Stop all monitors
    this.stopWebVitalsMonitoring();
    this.stopMemoryMonitoring();
    this.stopInteractionMonitoring();
    this.stopErrorMonitoring();
    this.stopNetworkMonitoring();
    this.stopLongTaskMonitoring();
    
    console.log('[Performance Monitor] All monitoring stopped');
  }

  installQueryInterceptor(queryClient: QueryClient): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled || this.queryInterceptorInstalled) return;

    const originalInvalidateQueries = queryClient.invalidateQueries.bind(queryClient);
    const originalSetQueryData = queryClient.setQueryData.bind(queryClient);
    const originalSetQueriesData = queryClient.setQueriesData.bind(queryClient);

    queryClient.invalidateQueries = async (filters?: any, options?: any) => {
      const startTime = performance.now();
      const queryKey = filters?.queryKey || 'all';
      const result = await originalInvalidateQueries(filters, options);
      const duration = performance.now() - startTime;
      this.logQuery(queryKey, 'invalidate', duration);
      return result;
    };

    queryClient.setQueryData = ((queryKey: any, updater: any) => {
      const startTime = performance.now();
      const result = originalSetQueryData(queryKey, updater);
      const duration = performance.now() - startTime;
      this.logQuery(queryKey, 'setData', duration, true);
      return result;
    }) as typeof queryClient.setQueryData;

    queryClient.setQueriesData = (filters: any, updater: any) => {
      const startTime = performance.now();
      const queryKey = filters?.queryKey || 'multiple';
      const result = originalSetQueriesData(filters, updater);
      const duration = performance.now() - startTime;
      this.logQuery(queryKey, 'setData', duration, true);
      return result;
    };

    this.queryInterceptorInstalled = true;
    console.log('[Performance Monitor] React Query interceptor installed');
  }

  // ============================================================================
  // Fetch Interceptor
  // ============================================================================

  private installFetchInterceptor(): void {
    if (process.env.NODE_ENV !== 'development' || window.fetch === this.interceptedFetch) return;
    
    window.fetch = this.interceptedFetch;
    console.log('[Performance Monitor] Fetch interceptor installed');
  }

  private uninstallFetchInterceptor(): void {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return;
    
    if (window.fetch === this.interceptedFetch) {
      window.fetch = this.originalFetch;
      console.log('[Performance Monitor] Fetch interceptor uninstalled');
    }
  }

  private interceptedFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) {
      return this.originalFetch(input, init);
    }

    const startTime = performance.now();
    const url = typeof input === 'string' ? input : input.toString();
    const method = init?.method || 'GET';
    
    let requestSize = 0;
    if (init?.body) {
      if (typeof init.body === 'string') {
        requestSize = new Blob([init.body]).size;
      } else if (init.body instanceof Blob) {
        requestSize = init.body.size;
      }
    }
    
    try {
      const response = await this.originalFetch(input, init);
      const duration = performance.now() - startTime;
      
      const clonedResponse = response.clone();
      let responseSize = 0;
      
      try {
        const blob = await clonedResponse.blob();
        responseSize = blob.size;
      } catch {
        const contentLength = response.headers.get('content-length');
        if (contentLength) responseSize = parseInt(contentLength, 10);
      }
      
      this.logFetch(url, method, duration, response.status, undefined, responseSize, requestSize);
      return response;
    } catch (error) {
      const duration = performance.now() - startTime;
      this.logFetch(url, method, duration, undefined, error as Error, 0, requestSize);
      throw error;
    }
  };

  // ============================================================================
  // Web Vitals Monitoring
  // ============================================================================

  private startWebVitalsMonitoring(): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;

    this.observeLCP();
    this.observeFID();
    this.observeCLS();
    this.observeNavigationTiming();
  }

  private stopWebVitalsMonitoring(): void {
    if (this.webVitalsObserver) {
      this.webVitalsObserver.disconnect();
      this.webVitalsObserver = null;
    }
  }

  private observeLCP(): void {
    if (!('PerformanceObserver' in window)) return;
    
    try {
      this.webVitalsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        if (lastEntry) {
          this.lcpValue = lastEntry.startTime;
          this.logWebVital('LCP', this.lcpValue, {
            element: (lastEntry as any).element?.tagName || 'unknown',
            url: (lastEntry as any).url || location.href,
            rating: this.getWebVitalRating('LCP', this.lcpValue),
          });
        }
      });

      this.webVitalsObserver.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (error) {
      console.warn('LCP monitoring not supported:', error);
    }
  }

  private observeFID(): void {
    if (!('PerformanceObserver' in window)) return;
    
    try {
      const fidObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const fidEntry = entry as any;
          this.fidValue = (fidEntry.processingStart || 0) - entry.startTime;
          
          this.logWebVital('FID', this.fidValue, {
            eventType: entry.name || 'unknown',
            target: fidEntry.target?.tagName || 'unknown',
            rating: this.getWebVitalRating('FID', this.fidValue),
          });
        });
      });

      fidObserver.observe({ entryTypes: ['first-input'] });
    } catch (error) {
      console.warn('FID monitoring not supported:', error);
    }
  }

  private observeCLS(): void {
    if (!('PerformanceObserver' in window)) return;
    
    try {
      const clsObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (!(entry as any).hadRecentInput) {
            this.clsValue += (entry as any).value;
            
            this.logWebVital('CLS', this.clsValue, {
              sources: (entry as any).sources?.map((source: any) => source.node?.tagName).join(', ') || 'unknown',
              rating: this.getWebVitalRating('CLS', this.clsValue),
            });
          }
        });
      });

      clsObserver.observe({ entryTypes: ['layout-shift'] });
    } catch (error) {
      console.warn('CLS monitoring not supported:', error);
    }
  }

  private observeNavigationTiming(): void {
    if (!('PerformanceObserver' in window)) return;
    
    try {
      const navObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const navEntry = entry as PerformanceNavigationTiming;
          
          const metrics = {
            domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart,
            loadComplete: navEntry.loadEventEnd - navEntry.loadEventStart,
            dnsLookup: navEntry.domainLookupEnd - navEntry.domainLookupStart,
            tcpConnect: navEntry.connectEnd - navEntry.connectStart,
            serverResponse: navEntry.responseEnd - navEntry.requestStart,
            domParsing: navEntry.domComplete - navEntry.domInteractive,
          };

          this.logNavigation('page-load', location.pathname, navEntry.loadEventEnd, {
            type: navEntry.type,
            redirectCount: navEntry.redirectCount,
            ...metrics,
          });
        });
      });

      navObserver.observe({ entryTypes: ['navigation'] });
    } catch (error) {
      console.warn('Navigation timing monitoring not supported:', error);
    }
  }

  private getWebVitalRating(metric: 'LCP' | 'FID' | 'CLS', value: number): 'good' | 'needs-improvement' | 'poor' {
    const thresholds = {
      LCP: { good: 2500, poor: 4000 },
      FID: { good: 100, poor: 300 },
      CLS: { good: 0.1, poor: 0.25 },
    };
    const threshold = thresholds[metric];
    if (value <= threshold.good) return 'good';
    if (value <= threshold.poor) return 'needs-improvement';
    return 'poor';
  }

  getCurrentVitals() {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return null;
    
    return {
      lcp: this.lcpValue,
      fid: this.fidValue,
      cls: this.clsValue,
      ratings: {
        lcp: this.getWebVitalRating('LCP', this.lcpValue),
        fid: this.getWebVitalRating('FID', this.fidValue),
        cls: this.getWebVitalRating('CLS', this.clsValue),
      },
    };
  }

  // ============================================================================
  // Memory Monitoring
  // ============================================================================

  private startMemoryMonitoring(): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    this.takeMemorySnapshot();
    this.memoryIntervalId = setInterval(() => {
      this.takeMemorySnapshot();
    }, 30000); // 30 seconds
  }

  private stopMemoryMonitoring(): void {
    if (this.memoryIntervalId) {
      clearInterval(this.memoryIntervalId);
      this.memoryIntervalId = null;
    }
  }

  private takeMemorySnapshot(): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;

    this.logMemoryUsage();

    if (this.lastMemorySnapshot && 'memory' in performance) {
      const memory = (performance as any).memory;
      const growth = memory.usedJSHeapSize - this.lastMemorySnapshot.usedJSHeapSize;
      
      if (growth > 10 * 1024 * 1024) { // More than 10MB growth
        this.logError(
          new Error('Potential memory leak detected'),
          'memory-leak-detection',
          {
            growthMB: Math.round(growth / (1024 * 1024)),
            currentUsedMB: Math.round(memory.usedJSHeapSize / (1024 * 1024)),
            previousUsedMB: Math.round(this.lastMemorySnapshot.usedJSHeapSize / (1024 * 1024)),
          }
        );
      }
    }

    if ('memory' in performance) {
      this.lastMemorySnapshot = { ...(performance as any).memory };
    }
  }

  getMemoryUsage() {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return null;
    
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return {
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
        usedMB: Math.round(memory.usedJSHeapSize / (1024 * 1024)),
        totalMB: Math.round(memory.totalJSHeapSize / (1024 * 1024)),
        limitMB: Math.round(memory.jsHeapSizeLimit / (1024 * 1024)),
        usagePercentage: Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100),
      };
    }
    return null;
  }

  // ============================================================================
  // Interaction Monitoring
  // ============================================================================

  private startInteractionMonitoring(): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    document.addEventListener('mousedown', this.handleMouseDown);
    document.addEventListener('click', this.handleClick);
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('input', this.handleInput);
    document.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  private stopInteractionMonitoring(): void {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return;
    
    document.removeEventListener('mousedown', this.handleMouseDown);
    document.removeEventListener('click', this.handleClick);
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('input', this.handleInput);
    document.removeEventListener('scroll', this.handleScroll);
    this.clickStartTimes.clear();
  }

  private handleMouseDown = (event: MouseEvent): void => {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    const target = event.target as Element;
    this.clickStartTimes.set(target, performance.now());
  };

  private handleClick = (event: MouseEvent): void => {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    const target = event.target as Element;
    const startTime = this.clickStartTimes.get(target);
    
    if (startTime) {
      const duration = performance.now() - startTime;
      const elementDescription = this.getElementDescription(target);
      
      this.logInteraction('click', elementDescription, duration, {
        x: event.clientX,
        y: event.clientY,
        button: event.button,
      });
      
      this.clickStartTimes.delete(target);
    }
  };

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    const target = event.target as Element;
    const elementDescription = this.getElementDescription(target);
    
    if (event.key === 'Enter' || event.key === 'Escape' || event.key === 'Tab' || event.ctrlKey || event.altKey) {
      this.logInteraction('keypress', elementDescription, 0, {
        key: event.key,
        code: event.code,
      });
    }
  };

  private handleInput = (event: Event): void => {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    const target = event.target as Element;
    const elementDescription = this.getElementDescription(target);
    
    this.logInteraction('input', elementDescription, 0, {
      inputType: (target as HTMLInputElement).type || 'text',
      valueLength: (target as HTMLInputElement).value?.length || 0,
    });
  };

  private handleScroll = (): void => {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    const startTime = performance.now();
    
    requestAnimationFrame(() => {
      const duration = performance.now() - startTime;
      
      this.logInteraction('scroll', 'window', duration, {
        scrollY: window.scrollY,
        scrollX: window.scrollX,
      });
    });
  };

  private getElementDescription(element: Element): string {
    const tagName = element.tagName.toLowerCase();
    const id = element.id ? `#${element.id}` : '';
    const dataTestId = element.getAttribute('data-testid') ? `[data-testid="${element.getAttribute('data-testid')}"]` : '';
    
    return `${tagName}${id}${dataTestId}` || 'unknown-element';
  }

  // ============================================================================
  // Error Monitoring
  // ============================================================================

  private startErrorMonitoring(): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    window.addEventListener('error', this.handleError);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    window.addEventListener('error', this.handleResourceError, true);
  }

  private stopErrorMonitoring(): void {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return;
    
    window.removeEventListener('error', this.handleError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    window.removeEventListener('error', this.handleResourceError, true);
  }

  private handleError = (event: ErrorEvent): void => {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    const error = event.error || new Error(event.message);
    this.logError(error, 'javascript-error', {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      message: event.message,
    });
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason));
    
    this.logError(error, 'unhandled-promise-rejection', {
      reason: String(event.reason),
    });
  };

  private handleResourceError = (event: Event): void => {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    const target = event.target as HTMLElement;
    
    if (target && target instanceof HTMLElement && (target.tagName === 'IMG' || target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
      const resourceUrl = (target as any).src || (target as any).href || 'unknown';
      const error = new Error(`Failed to load resource: ${resourceUrl}`);
      
      this.logError(error, 'resource-load-error', {
        resourceType: target.tagName.toLowerCase(),
        resourceUrl,
      });
    }
  };

  trackUserAction(action: string): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    this.recentActions.push({
      action,
      timestamp: Date.now()
    });
    
    if (this.recentActions.length > this.maxRecentActions) {
      this.recentActions = this.recentActions.slice(-this.maxRecentActions);
    }
  }

  // ============================================================================
  // Network Monitoring
  // ============================================================================

  private startNetworkMonitoring(): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    
    if ('connection' in navigator || 'mozConnection' in navigator || 'webkitConnection' in navigator) {
      // @ts-ignore
      this.connectionInfo = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      
      if (this.connectionInfo) {
        this.connectionInfo.addEventListener('change', this.handleConnectionChange);
        this.logCurrentConnection();
      }
    }
    
    this.logNetworkChange(navigator.onLine, this.connectionInfo);
  }

  private stopNetworkMonitoring(): void {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return;
    
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    
    if (this.connectionInfo) {
      this.connectionInfo.removeEventListener('change', this.handleConnectionChange);
    }
  }

  private handleOnline = (): void => {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    this.logNetworkChange(true, this.connectionInfo);
  };

  private handleOffline = (): void => {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    this.logNetworkChange(false, this.connectionInfo);
  };

  private handleConnectionChange = (): void => {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    this.logCurrentConnection();
  };

  private logCurrentConnection(): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled || !this.connectionInfo) return;
    
    this.logNetworkChange(navigator.onLine, {
      effectiveType: this.connectionInfo.effectiveType,
      downlink: this.connectionInfo.downlink,
      rtt: this.connectionInfo.rtt,
      saveData: this.connectionInfo.saveData,
      type: this.connectionInfo.type,
    });
  }

  getCurrentNetworkStatus() {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return null;
    
    return {
      online: navigator.onLine,
      connectionType: this.connectionInfo?.effectiveType || 'unknown',
      downlink: this.connectionInfo?.downlink,
      rtt: this.connectionInfo?.rtt,
      saveData: this.connectionInfo?.saveData,
    };
  }

  // ============================================================================
  // Long Task Monitoring
  // ============================================================================

  private startLongTaskMonitoring(): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    if ('PerformanceObserver' in window && PerformanceObserver.supportedEntryTypes?.includes('longtask')) {
      try {
        this.longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 50) {
              const attribution = (entry as any).attribution?.[0] || {};
              
              this.logLongTask(entry.duration, {
                name: entry.name,
                startTime: entry.startTime,
                containerType: attribution.containerType,
                containerSrc: attribution.containerSrc,
                containerId: attribution.containerId,
                containerName: attribution.containerName,
              });
            }
          }
        });

        this.longTaskObserver.observe({ entryTypes: ['longtask'] });
      } catch (error) {
        console.warn('Long task monitoring not supported:', error);
      }
    }
  }

  private stopLongTaskMonitoring(): void {
    if (this.longTaskObserver) {
      this.longTaskObserver.disconnect();
      this.longTaskObserver = null;
    }
  }

  // ============================================================================
  // Baselines & AI Annotations
  // ============================================================================

  private loadBaselines(): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    try {
      const stored = localStorage.getItem('performance-baselines');
      if (stored) {
        const data = JSON.parse(stored);
        this.baselines = new Map(Object.entries(data));
      }
    } catch (e) {
      console.error('Failed to load performance baselines', e);
    }
  }

  private saveBaselines(): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    try {
      const data: Record<string, PerformanceBaseline> = {};
      this.baselines.forEach((value, key) => {
        data[key] = value;
      });
      localStorage.setItem('performance-baselines', JSON.stringify(data));
    } catch (e) {
      console.error('Failed to save performance baselines', e);
    }
  }

  private updateBaseline(operationKey: string, duration: number): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    const baseline = this.baselines.get(operationKey);
    
    if (!baseline) {
      this.baselines.set(operationKey, {
        operationType: operationKey,
        averageDuration: duration,
        minDuration: duration,
        maxDuration: duration,
        count: 1,
        standardDeviation: 0
      });
    } else {
      const newCount = baseline.count + 1;
      const newAverage = ((baseline.averageDuration * baseline.count) + duration) / newCount;
      
      const squaredDiffs = baseline.standardDeviation * baseline.standardDeviation * baseline.count;
      const newSquaredDiff = Math.pow(duration - newAverage, 2);
      const newVariance = (squaredDiffs + newSquaredDiff) / newCount;
      const newStdDev = Math.sqrt(newVariance);
      
      this.baselines.set(operationKey, {
        operationType: operationKey,
        averageDuration: newAverage,
        minDuration: Math.min(baseline.minDuration, duration),
        maxDuration: Math.max(baseline.maxDuration, duration),
        count: newCount,
        standardDeviation: newStdDev
      });
    }
    
    if (this.baselines.get(operationKey)!.count % 10 === 0) {
      this.saveBaselines();
    }
  }

  private getPerformanceRating(operationKey: string, duration: number): NonNullable<PerformanceLog['aiAnnotation']> {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ {
      return { performanceRating: 'good' };
    }
    
    const baseline = this.baselines.get(operationKey);
    
    if (!baseline) {
      return {
        performanceRating: 'good',
        suggestion: 'First measurement for this operation. Building baseline.'
      };
    }
    
    const deviation = ((duration - baseline.averageDuration) / baseline.averageDuration) * 100;
    const isAnomaly = Math.abs(duration - baseline.averageDuration) > (2 * baseline.standardDeviation);
    
    let rating: 'excellent' | 'good' | 'acceptable' | 'slow' | 'critical' = 'good';
    let suggestion: string | undefined;
    
    if (duration < baseline.averageDuration * 0.7) {
      rating = 'excellent';
      suggestion = 'Performance is significantly better than average.';
    } else if (duration < baseline.averageDuration * 1.2) {
      rating = 'good';
    } else if (duration < baseline.averageDuration * 1.5) {
      rating = 'acceptable';
      suggestion = 'Performance is slightly below average. Monitor for trends.';
    } else if (duration < baseline.averageDuration * 2) {
      rating = 'slow';
      suggestion = 'Performance degradation detected. Investigate potential bottlenecks.';
    } else {
      rating = 'critical';
      suggestion = 'Severe performance degradation. Immediate investigation required.';
    }
    
    return {
      performanceRating: rating,
      deviationFromBaseline: deviation,
      isAnomaly,
      suggestion
    };
  }

  // ============================================================================
  // Logging Methods
  // ============================================================================

  setCorrelationId(correlationId?: string): void {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return;
    this.correlationId = correlationId;
  }

  startOperation(operationId: string): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    this.startTimes.set(operationId, performance.now());
  }

  endOperation(
    operationId: string,
    type: PerformanceLog['type'],
    operation: string,
    metadata?: Record<string, any>,
    status: PerformanceLog['status'] = 'success'
  ): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;

    const startTime = this.startTimes.get(operationId);
    if (!startTime) return;

    const duration = performance.now() - startTime;
    this.startTimes.delete(operationId);

    const operationKey = `${type}:${operation}`;
    this.updateBaseline(operationKey, duration);
    const aiAnnotation = this.getPerformanceRating(operationKey, duration);

    this.addLog({
      id: operationId,
      timestamp: Date.now(),
      type,
      operation,
      duration,
      metadata,
      status,
      correlationId: this.correlationId,
      aiAnnotation
    });
  }

  logFetch(
    url: string,
    method: string,
    duration: number,
    statusCode?: number,
    error?: Error,
    responseSize?: number,
    requestSize?: number
  ): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;

    const operationKey = `fetch:${method}:${url}`;
    this.updateBaseline(operationKey, duration);
    const aiAnnotation = this.getPerformanceRating(operationKey, duration);

    this.addLog({
      id: `fetch-${Date.now()}-${Math.random()}`,
      timestamp: Date.now(),
      type: 'fetch',
      operation: `${method} ${url}`,
      duration,
      url,
      method,
      statusCode,
      responseSize,
      requestSize,
      status: error ? 'error' : (statusCode && statusCode >= 400 ? 'warning' : 'success'),
      errorMessage: error?.message,
      correlationId: this.correlationId,
      aiAnnotation,
      metadata: {
        slowRequest: duration > 3000,
        largeResponse: responseSize && responseSize > 1000000,
      },
    });
  }

  logQuery(
    queryKey: string | string[],
    operation: 'fetch' | 'invalidate' | 'setData',
    duration: number,
    cacheHit: boolean = false
  ): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;

    const key = Array.isArray(queryKey) ? queryKey.join(':') : queryKey;
    const operationKey = `query:${operation}:${key}`;
    this.updateBaseline(operationKey, duration);
    const aiAnnotation = this.getPerformanceRating(operationKey, duration);
    
    this.addLog({
      id: `query-${Date.now()}-${Math.random()}`,
      timestamp: Date.now(),
      type: 'query',
      operation: `${operation}: ${key}`,
      duration,
      cacheHit,
      status: 'success',
      correlationId: this.correlationId,
      aiAnnotation,
      metadata: { queryKey, operation },
    });
  }

  logRender(componentName: string, duration: number, props?: Record<string, any>): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;

    const operationKey = `render:${componentName}`;
    this.updateBaseline(operationKey, duration);
    const aiAnnotation = this.getPerformanceRating(operationKey, duration);

    this.addLog({
      id: `render-${Date.now()}-${Math.random()}`,
      timestamp: Date.now(),
      type: 'render',
      operation: `Render ${componentName}`,
      duration,
      status: 'success',
      correlationId: this.correlationId,
      aiAnnotation,
      metadata: { componentName, props },
    });
  }

  logWebVital(
    metric: 'LCP' | 'FID' | 'CLS',
    value: number,
    metadata?: Record<string, any>
  ): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;

    const vitalData: Partial<PerformanceLog> = {
      lcp: metric === 'LCP' ? value : undefined,
      fid: metric === 'FID' ? value : undefined,
      cls: metric === 'CLS' ? value : undefined,
    };

    this.addLog({
      id: `vital-${metric.toLowerCase()}-${Date.now()}`,
      timestamp: Date.now(),
      type: 'vitals',
      operation: metric,
      duration: value,
      metadata,
      status: 'success',
      correlationId: this.correlationId,
      ...vitalData,
    });
  }

  logMemoryUsage(): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    // @ts-ignore
    if ('memory' in performance) {
      // @ts-ignore
      const memory = performance.memory as {
        usedJSHeapSize: number;
        totalJSHeapSize: number;
        jsHeapSizeLimit: number;
      };
      
      this.addLog({
        id: `memory-${Date.now()}`,
        timestamp: Date.now(),
        type: 'memory',
        operation: 'heap-snapshot',
        duration: 0,
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
        status: 'success',
        correlationId: this.correlationId,
      });
    }
  }

  logNavigation(
    from: string,
    to: string,
    duration: number,
    metadata?: Record<string, any>
  ): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;

    const operationKey = `navigation:${from}->${to}`;
    this.updateBaseline(operationKey, duration);
    const aiAnnotation = this.getPerformanceRating(operationKey, duration);

    this.addLog({
      id: `nav-${Date.now()}`,
      timestamp: Date.now(),
      type: 'navigation',
      operation: `${from} -> ${to}`,
      duration,
      routeFrom: from,
      routeTo: to,
      metadata,
      status: 'success',
      correlationId: this.correlationId,
      aiAnnotation,
    });
  }

  logInteraction(
    type: 'click' | 'input' | 'scroll' | 'keypress',
    targetElement: string,
    duration: number,
    metadata?: Record<string, any>
  ): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;

    this.addLog({
      id: `interaction-${type}-${Date.now()}`,
      timestamp: Date.now(),
      type: 'interaction',
      operation: `${type} on ${targetElement}`,
      duration,
      targetElement,
      interactionType: type,
      metadata,
      status: 'success',
      correlationId: this.correlationId,
    });
  }

  logError(
    error: Error,
    operation: string,
    metadata?: Record<string, any>
  ): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;

    this.addLog({
      id: `error-${Date.now()}`,
      timestamp: Date.now(),
      type: 'error',
      operation,
      duration: 0,
      errorMessage: error.message,
      errorStack: error.stack,
      pageVisible: !document.hidden,
      windowFocused: document.hasFocus(),
      correlationId: this.correlationId,
      metadata: {
        ...metadata,
        stack: error.stack,
        name: error.name,
        userAgent: navigator.userAgent,
      },
      status: 'error',
    });
  }

  logWebSocket(event: string, duration: number = 0, metadata?: Record<string, any>): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    this.addLog({
      id: `ws-${Date.now()}-${Math.random()}`,
      timestamp: Date.now(),
      type: 'websocket',
      operation: `WebSocket: ${event}`,
      duration,
      status: metadata?.error ? 'error' : 'success',
      wsState: metadata?.readyState,
      wsReconnectAttempts: metadata?.reconnectAttempts,
      correlationId: this.correlationId,
      metadata,
    });
  }

  logNetworkChange(online: boolean, connectionInfo?: any): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    this.addLog({
      id: `network-${Date.now()}`,
      timestamp: Date.now(),
      type: 'network',
      operation: online ? 'Network Online' : 'Network Offline',
      duration: 0,
      status: online ? 'success' : 'warning',
      connectionType: connectionInfo?.effectiveType,
      effectiveBandwidth: connectionInfo?.downlink,
      correlationId: this.correlationId,
      metadata: {
        online,
        rtt: connectionInfo?.rtt,
        saveData: connectionInfo?.saveData,
      },
    });
  }

  logLongTask(duration: number, attribution?: any): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    const aiAnnotation: PerformanceLog['aiAnnotation'] = {
      performanceRating: duration > 100 ? 'critical' : duration > 50 ? 'slow' : 'acceptable',
      suggestion: duration > 100 
        ? 'Long task blocking main thread. Break into smaller chunks or use Web Workers.'
        : duration > 50 
        ? 'Task duration approaching threshold. Consider optimization.'
        : undefined
    };
    
    this.addLog({
      id: `longtask-${Date.now()}`,
      timestamp: Date.now(),
      type: 'longtask',
      operation: 'Main Thread Blocked',
      duration,
      status: duration > 100 ? 'warning' : 'success',
      correlationId: this.correlationId,
      aiAnnotation,
      metadata: {
        attribution,
        threshold: 50,
      },
    });
  }

  logComponentLifecycle(
    componentName: string, 
    phase: 'mount' | 'update' | 'unmount' | 'render' | 'batch-render',
    duration: number,
    metadata?: string[] | Record<string, any>
  ): void {
    if (process.env.NODE_ENV !== 'development' || !this.enabled) return;
    
    const operationKey = `lifecycle:${componentName}:${phase}`;
    this.updateBaseline(operationKey, duration);
    const aiAnnotation = this.getPerformanceRating(operationKey, duration);
    
    // Handle metadata - can be either string[] (propsChanged) or object
    const propsChanged = Array.isArray(metadata) ? metadata : undefined;
    const renderMetadata = !Array.isArray(metadata) && metadata ? metadata : undefined;
    
    this.addLog({
      id: `lifecycle-${Date.now()}-${Math.random()}`,
      timestamp: Date.now(),
      type: 'render',
      operation: `${componentName} ${phase}`,
      duration,
      componentPhase: phase,
      propsChanged,
      renderMetadata,
      status: 'success',
      correlationId: this.correlationId,
      aiAnnotation,
      metadata: {
        componentName,
        phase,
        ...(propsChanged && { propsChanged }),
        ...(renderMetadata && renderMetadata),
      },
    });
  }

  private addLog(log: PerformanceLog): void {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return;
    
    this.logs.push(log);
    
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  // ============================================================================
  // Query & Statistics Methods
  // ============================================================================

  getLogs(): PerformanceLog[] {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return [];
    return [...this.logs];
  }

  getLogsByType(type: PerformanceLog['type']): PerformanceLog[] {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return [];
    return this.logs.filter(log => log.type === type);
  }

  getBaselines(): Map<string, PerformanceBaseline> {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return new Map();
    return new Map(this.baselines);
  }

  getStatistics() {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ {
      return {
        totalLogs: 0,
        byType: {},
        averageDuration: {},
        slowestOperations: [],
        errorRate: 0,
        cacheHitRate: 0,
        performanceRatings: { excellent: 0, good: 0, acceptable: 0, slow: 0, critical: 0 },
        anomalyCount: 0,
      };
    }
    
    const stats = {
      totalLogs: this.logs.length,
      byType: {} as Record<string, number>,
      averageDuration: {} as Record<string, number>,
      slowestOperations: [] as PerformanceLog[],
      errorRate: 0,
      cacheHitRate: 0,
      performanceRatings: {
        excellent: 0,
        good: 0,
        acceptable: 0,
        slow: 0,
        critical: 0
      },
      anomalyCount: 0,
    };

    const typeGroups = this.logs.reduce((acc, log) => {
      if (!acc[log.type]) acc[log.type] = [];
      acc[log.type].push(log);
      return acc;
    }, {} as Record<string, PerformanceLog[]>);

    Object.entries(typeGroups).forEach(([type, logs]) => {
      stats.byType[type] = logs.length;
      const totalDuration = logs.reduce((sum, log) => sum + log.duration, 0);
      stats.averageDuration[type] = totalDuration / logs.length;
    });

    stats.slowestOperations = [...this.logs]
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 10);

    const errors = this.logs.filter(log => log.status === 'error').length;
    stats.errorRate = this.logs.length > 0 ? (errors / this.logs.length) * 100 : 0;

    const queries = this.logs.filter(log => log.type === 'query');
    const cacheHits = queries.filter(log => log.cacheHit).length;
    stats.cacheHitRate = queries.length > 0 ? (cacheHits / queries.length) * 100 : 0;

    this.logs.forEach(log => {
      if (log.aiAnnotation?.performanceRating) {
        stats.performanceRatings[log.aiAnnotation.performanceRating]++;
      }
      if (log.aiAnnotation?.isAnomaly) {
        stats.anomalyCount++;
      }
    });

    return stats;
  }

  exportAsJSON(): string {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return '{}';
    
    const exportData = {
      exportDate: new Date().toISOString(),
      totalLogs: this.logs.length,
      timeRange: {
        start: this.logs[0]?.timestamp ? new Date(this.logs[0].timestamp).toISOString() : null,
        end: this.logs[this.logs.length - 1]?.timestamp 
          ? new Date(this.logs[this.logs.length - 1].timestamp).toISOString() 
          : null,
      },
      statistics: this.getStatistics(),
      baselines: Array.from(this.baselines.entries()).map(([key, baseline]) => ({
        operation: key,
        ...baseline
      })),
      logs: this.logs.map(log => ({
        ...log,
        timestampFormatted: new Date(log.timestamp).toISOString(),
      })),
      aiAnalysis: {
        performanceAnomalies: this.logs.filter(log => log.aiAnnotation?.isAnomaly),
        criticalPerformanceIssues: this.logs.filter(log => log.aiAnnotation?.performanceRating === 'critical'),
        slowOperations: this.logs.filter(log => log.aiAnnotation?.performanceRating === 'slow'),
      }
    };

    return JSON.stringify(exportData, null, 2);
  }

  exportAsCSV(): string {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return '';
    
    const headers = [
      'Timestamp',
      'Type',
      'Operation',
      'Duration (ms)',
      'Status',
      'Performance Rating',
      'URL',
      'Method',
      'Status Code',
      'Error Message',
    ];

    const rows = this.logs.map(log => [
      new Date(log.timestamp).toISOString(),
      log.type,
      log.operation,
      log.duration.toFixed(2),
      log.status,
      log.aiAnnotation?.performanceRating || 'N/A',
      log.url || '',
      log.method || '',
      log.statusCode?.toString() || '',
      log.errorMessage || '',
    ]);

    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  }

  clear(): void {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return;
    this.logs = [];
  }

  // ============================================================================
  // Summary & Export
  // ============================================================================

  getPerformanceSummary() {
    if (!shouldAllowMonitoring()) /* DEBUG_TEMP: was (process.env.NODE_ENV !== 'development') */ return null;
    
    const logs = this.getLogs();
    const stats = this.getStatistics();
    const webVitals = this.getCurrentVitals();
    const memoryInfo = this.getMemoryUsage();

    return {
      isEnabled: this.isEnabled(),
      totalLogs: logs.length,
      statistics: stats,
      webVitals,
      memoryInfo,
      recentErrors: logs.filter(log => log.type === 'error').slice(-5),
      recentSlowQueries: logs
        .filter(log => log.type === 'query' && log.duration > 1000)
        .slice(-5),
      recentSlowFetches: logs
        .filter(log => log.type === 'fetch' && log.duration > 2000)
        .slice(-5),
      recentLongTasks: logs
        .filter(log => log.type === 'longtask')
        .slice(-10),
      networkStatus: this.getCurrentNetworkStatus(),
    };
  }
}

// ============================================================================
// Singleton Export & Auto-Initialization
// ============================================================================

export const performanceMonitor = new PerformanceMonitor();

// Auto-initialize monitoring if it was previously enabled
// DEBUG_TEMP: Changed from (process.env.NODE_ENV === 'development') to shouldAllowMonitoring()
// REVERT: Change back to: if (process.env.NODE_ENV === 'development') { ... }
if (shouldAllowMonitoring()) {
  if (performanceMonitor.isEnabled()) {
    console.log('[Performance Monitor] Starting performance monitoring (previously enabled)');
    performanceMonitor.startAllMonitoring();
  } else {
    console.log('[Performance Monitor] Performance monitoring disabled');
  }
}

// Backwards compatibility exports
export const performanceLogger = performanceMonitor;
export const performanceMonitorManager = {
  startAllMonitoring: () => performanceMonitor.startAllMonitoring(),
  stopAllMonitoring: () => performanceMonitor.stopAllMonitoring(),
  getPerformanceSummary: () => performanceMonitor.getPerformanceSummary(),
  exportPerformanceData: () => ({
    timestamp: new Date().toISOString(),
    summary: performanceMonitor.getPerformanceSummary(),
    logs: performanceMonitor.getLogs(),
    userAgent: navigator.userAgent,
    url: window.location.href,
  }),
};
